import React from 'react'
import { useSelector } from 'react-redux'
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router'
import HomePage from 'pages/HomePage'
import HospitalProfile from 'pages/Hospitals/Profile/index'
import UserProfile from 'pages/Users/Profile'
import EmployerCandidateMatchListPage from 'pages/Hospitals/Candidates/EmployerCandidateMatchListPage'
import JobFormPage from 'pages/Hospitals/Jobs/JobFormPage'
import JobsList from 'pages/Jobs/index'
import JobDetails from 'pages/Jobs/JobDetails'
import PhysiciansList from 'pages/Physicians/index'
import Connections from 'pages/Hospitals/Connections/index'
import About from 'pages/About/index'
import Employers from './pages/Super/Employers'
import EmployerUsers from './pages/Super/EmployerUsers'
import Login, { useIsUser } from 'pages/Identity/Login'
import CreatePassword from 'pages/Identity/CreatePassword'
import ResendConfirmationLink from 'pages/Identity/ResendConfirmationLink'
import SendResetPasswordLink from 'pages/Identity/SendResetPasswordLink'
import ResetPassword from 'pages/Identity/ResetPassword'
import EmailConfirmation from 'pages/Register/EmailConfirmation'
import Support from 'pages/Contact/Support.js'
import ContactUs from 'pages/Contact/ContactUs.js'
import { ReduxRouter } from '@lagunovsky/redux-react-router'
import Filters from 'pages/Users/Filters'
import Alerts from 'pages/Users/Alerts'
import PhysicianRegister from 'pages/Register/Physicians'
import Layout from 'components/Layout'
import TermsAndConditionsV2 from 'pages/Legal/TermsAndConditionsV2'
import PrivacyPolicyV2 from 'pages/Legal/PrivacyPolicyV2'
import EmployerServiceAgreementV1 from 'pages/Legal/EmployerServiceAgreementV1'
import CandidatesSuper from 'pages/Super/Candidates'
import PartnerInterest from 'pages/Super/PartnerInterest'
import JobInterest from './pages/Super/JobInterest'
import Users from './pages/Admin/Users'
import Onboarding from './pages/Onboarding'
import CandidateProfile from './pages/Users/Profile/CandidateProfile'
import EmployerUserProfile from './pages/Users/Profile/EmployerProfile'
import Housing from './pages/Housing'
import Applications from './pages/Applications'
import SuperJobs from './pages/Super/Jobs'
import JobDetailPage from './pages/Jobs/AI/JobDetailPage'
import Directory from './pages/Directory'
import Salary from './pages/Salary'
import Careers from './pages/Careers'
import ResidencyTransfer from './pages/ResidencyTransfer'
import SuperHousing from './pages/Super/Housing'
import BackgroundJobLauncher from './pages/Super/BackgroundJobLauncher'
import ScheduledBackgroundJobs from './pages/Super/ScheduledBackgroundJobs'
import ChatbotDashboard from './pages/Super/ChatbotDashboard'
import ChatbotConversations from './pages/Super/ChatbotConversations'
import EmployerKioskPage from './pages/Super/EmployerKioskPage'
import EmployerUserSignups from './pages/Super/EmployerUserSignups'
import SpecialtySynonyms from './pages/Super/SpecialtySynonyms'
import Resources from './pages/Resources'
import AAPPR2026 from './pages/AAPPR2026'
import EmployerCandidateProfilePage from './pages/Hospitals/Candidates/EmployerCandidateProfilePage'
import EmployerRegister from './pages/Register/Employers'
import SubscriptionSuccessPage from './pages/Register/Employers/SubscriptionSuccessPage'
import EmployerLandingPage from 'pages/Employers/EmployerLandingPage'
import RecruiterLandingPage from './pages/Recruiters'
import PricingPage from './pages/Pricing'
import RequestDemo from './pages/Contact/RequestDemo'
import UpgradePlanDialog from 'src/components/UpgradePlanDialog'



const PrivateRoute = ({ children }) => {
  const location = useLocation()
  const signedIn = useSelector(state => state.identity.signedIn)
  if (!signedIn) {
    return (
      <Navigate to='/login' state={{ from: `${location.pathname}${location.search}` }} replace />
    )
  }

  return children
}

const PublicPrivateRoute = ({ children }) => {
  const location = useLocation()
  const signedIn = useSelector(state => state.identity.signedIn)
  if (location.search) {
    const queryParams = new URLSearchParams(location.search)
    const shouldLogin =
      queryParams.get('login') === 'true' || queryParams.get('clickFrom') === 'email'
    if (!signedIn && shouldLogin) {
      queryParams.delete('login')
      return (
        <Navigate
          to='/login'
          state={{ from: `${location.pathname}?${queryParams.toString()}` }}
          replace
        />
      )
    }
  }

  return children
}

// The paywall is currently enforced client-side only. Server-side subscription checks should be
// added before any sensitive data (e.g. candidate PII) depends on subscription gating.
const PaywalledRoute = ({ children }) => {
  const navigate = useNavigate()
  const { isEmployerUser, isSuper, hasActiveSubscription } = useIsUser()

  if (isEmployerUser && !isSuper && !hasActiveSubscription) {
    return (
      <UpgradePlanDialog
        open
        onClose={() => navigate('/jobs')}
      />
    )
  }

  return children
}

// TODO:BROCK nested routes and outlets could cut down on cruft here: https://blog.logrocket.com/complete-guide-authentication-with-react-router-v6/

export default history => {
  return (
    <ReduxRouter history={history}>
      <Layout>
        <Routes>
          <Route path='/' element={<HomePage />} />

          <Route path='/login' element={<Login />} />
          <Route path='/housing' element={<Housing />} />
          <Route path='/directory' element={<Directory />} />
          <Route path='/salary' element={<Salary />} />
          <Route path='/residency-transfer' element={<ResidencyTransfer />} />

          <Route path='/candidate/email_confirmation_prompt' element={<EmailConfirmation />} />
          <Route path='/create_password' element={<CreatePassword />} />
          <Route path='/resend_confirmation_link' element={<ResendConfirmationLink />} />
          <Route path='/send_reset_password_link' element={<SendResetPasswordLink />} />
          <Route path='/reset_password' element={<ResetPassword />} />

          <Route path='/contact' element={<ContactUs />} />
          <Route path='/demo' element={<RequestDemo />} />
          <Route path='/about' element={<About />} />
          <Route path='/register/hospitals' element={<Navigate to='/register/employers' replace />} />
          <Route path='/register/physicians' element={<PhysicianRegister />} />
          <Route path='/employers' element={<EmployerLandingPage />} />
          <Route path='/recruiters' element={<RecruiterLandingPage />} />
          <Route path='/register/employers' element={<EmployerRegister />} />
          <Route path='/register/employers/subscription_success' element={<SubscriptionSuccessPage />} />
          <Route path='/pricing' element={<PricingPage />} />
          <Route path='/resources' element={<Resources />} />
          <Route path='/aappr-2026' element={<AAPPR2026 />} />
          <Route
            path='/super/jobs'
            element={
              <PrivateRoute>
                <SuperJobs />
              </PrivateRoute>
            }
          />
          <Route
            path='/super/employers'
            element={
              <PrivateRoute>
                <Employers />
              </PrivateRoute>
            }
          />
          <Route
            path='/super/employer_users'
            element={
              <PrivateRoute>
                <EmployerUsers />
              </PrivateRoute>
            }
          />
          <Route
            path='/admin/users'
            element={
              <PrivateRoute>
                <PaywalledRoute>
                  <Users />
                </PaywalledRoute>
              </PrivateRoute>
            }
          />
          <Route
            path='/super/candidates'
            element={
              <PrivateRoute>
                <CandidatesSuper />
              </PrivateRoute>
            }
          />
          <Route
            path='/super/partner_interest'
            element={
              <PrivateRoute>
                <PartnerInterest />
              </PrivateRoute>
            }
          />
          <Route
            path='/super/job_interest'
            element={
              <PrivateRoute>
                <JobInterest />
              </PrivateRoute>
            }
          />
          <Route
            path='/super/housing'
            element={
              <PrivateRoute>
                <SuperHousing />
              </PrivateRoute>
            }
          />
          <Route
            path='/super/background_jobs'
            element={
              <PrivateRoute>
                <BackgroundJobLauncher />
              </PrivateRoute>
            }
          />
          <Route
            path='/super/scheduled-background-jobs'
            element={
              <PrivateRoute>
                <ScheduledBackgroundJobs />
              </PrivateRoute>
            }
          />
          <Route
            path='/super/chatbot_dashboard'
            element={
              <PrivateRoute>
                <ChatbotDashboard />
              </PrivateRoute>
            }
          />
          <Route
            path='/super/chatbot_conversations'
            element={
              <PrivateRoute>
                <ChatbotConversations />
              </PrivateRoute>
            }
          />
          <Route
            path='/super/employer-kiosk'
            element={
              <PrivateRoute>
                <EmployerKioskPage />
              </PrivateRoute>
            }
          />
          <Route
            path='/super/employer_user_signups'
            element={
              <PrivateRoute>
                <EmployerUserSignups />
              </PrivateRoute>
            }
          />
          <Route
            path='/super/specialty_synonyms'
            element={
              <PrivateRoute>
                <SpecialtySynonyms />
              </PrivateRoute>
            }
          />
          <Route
            path='/hospitals/profile'
            element={
              <PrivateRoute>
                <PaywalledRoute>
                  <HospitalProfile />
                </PaywalledRoute>
              </PrivateRoute>
            }
          />
          <Route
            path='/hospitals/candidates/:id'
            element={
              <PrivateRoute>
                <PaywalledRoute>
                  <EmployerCandidateProfilePage />
                </PaywalledRoute>
              </PrivateRoute>
            }
          />
          <Route
            path='/hospitals/candidates'
            element={
              <PrivateRoute>
                <EmployerCandidateMatchListPage />
              </PrivateRoute>
            }
          />
          <Route
            path='/hospitals/jobs/new'
            element={
              <PrivateRoute>
                <JobFormPage />
              </PrivateRoute>
            }
          />
          <Route
            path='/hospitals/jobs/:id/edit'
            element={
              <PrivateRoute>
                <JobFormPage />
              </PrivateRoute>
            }
          />
          <Route
            path='/onboarding'
            element={
              <PrivateRoute>
                <Onboarding />
              </PrivateRoute>
            }
          />
          <Route
            path='/hospitals/connections'
            element={
              <PrivateRoute>
                <PaywalledRoute>
                  <Connections />
                </PaywalledRoute>
              </PrivateRoute>
            }
          />

          <Route
            path='/jobs/ai/:id'
            element={
              <PublicPrivateRoute>
                <JobDetailPage />
              </PublicPrivateRoute>
            }
          />
          <Route
            path='/jobs'
            element={
              <PublicPrivateRoute>
                <JobsList />
              </PublicPrivateRoute>
            }
          />
          <Route path='/jobs/:id' element={<JobDetails />} />
          <Route path='/dashboard/jobs/:id' element={<JobDetails />} />
          <Route
            path='/applications'
            element={
              <PrivateRoute>
                <Applications />
              </PrivateRoute>
            }
          />
          <Route
            path='/physicians'
            element={
              <PrivateRoute>
                <PaywalledRoute>
                  <PhysiciansList />
                </PaywalledRoute>
              </PrivateRoute>
            }
          />

          <Route
            path='/users/candidate/profile/'
            element={
              <PrivateRoute>
                <CandidateProfile />
              </PrivateRoute>
            }
          />
          <Route
            path='/users/employer_user/profile/'
            element={
              <PrivateRoute>
                <EmployerUserProfile />
              </PrivateRoute>
            }
          />
          <Route
            path='/users/profile/:id'
            element={
              <PrivateRoute>
                <PaywalledRoute>
                  <UserProfile />
                </PaywalledRoute>
              </PrivateRoute>
            }
          />
          <Route
            path='/users/filters'
            element={
              <PrivateRoute>
                <PaywalledRoute>
                  <Filters />
                </PaywalledRoute>
              </PrivateRoute>
            }
          />

          <Route
            path='/users/alerts'
            element={
              <PrivateRoute>
                <PaywalledRoute>
                  <Alerts />
                </PaywalledRoute>
              </PrivateRoute>
            }
          />
          <Route
            path='users/alerts/alert-records'
            element={
              <PrivateRoute>
                <PaywalledRoute>
                  <Alerts />
                </PaywalledRoute>
              </PrivateRoute>
            }
          />
          <Route path='/recruiters/profile/:id' element={<UserProfile />} />
          <Route
            path='/support'
            element={
              <PrivateRoute>
                <Support />
              </PrivateRoute>
            }
          />
          <Route path='/tac' element={<TermsAndConditionsV2 />} />
          <Route path='/privacy' element={<PrivacyPolicyV2 />} />
          <Route path='/employer-terms' element={<EmployerServiceAgreementV1 />} />
          <Route path='/careers' element={<Careers />} />
        </Routes>
      </Layout>
    </ReduxRouter>
  )
}
